import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check } from 'lucide-react';
import { cn } from '../lib/cn';

export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
export const DropdownMenuGroup = DropdownMenuPrimitive.Group;
export const DropdownMenuSub = DropdownMenuPrimitive.Sub;
export const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

export type DropdownMenuContentProps = ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Content
>;

export const DropdownMenuContent = forwardRef<HTMLDivElement, DropdownMenuContentProps>(
  function DropdownMenuContent({ className, sideOffset = 4, align = 'end', ...rest }, ref) {
    return (
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          align={align}
          collisionPadding={8}
          className={cn(
            'z-[--z-dropdown] min-w-48 overflow-hidden rounded-md p-1',
            'border border-border-subtle bg-surface-overlay shadow-e3',
            'data-[state=open]:animate-slide-up',
            className,
          )}
          {...rest}
        />
      </DropdownMenuPrimitive.Portal>
    );
  },
);

const ITEM_BASE = [
  'relative flex cursor-default select-none items-center gap-2 rounded-sm',
  'px-2 py-1.5 text-sm outline-none',
  'data-[highlighted]:bg-surface-hover',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  '[&_svg]:size-4 [&_svg]:shrink-0',
];

export type DropdownMenuItemProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
  /** Destructive items are tinted AND always paired with an icon — never colour alone. */
  destructive?: boolean;
};

export const DropdownMenuItem = forwardRef<HTMLDivElement, DropdownMenuItemProps>(
  function DropdownMenuItem({ className, destructive, ...rest }, ref) {
    return (
      <DropdownMenuPrimitive.Item
        ref={ref}
        className={cn(
          ITEM_BASE,
          destructive ? 'text-danger-fg data-[highlighted]:bg-danger-subtle' : 'text-text-primary',
          className,
        )}
        {...rest}
      />
    );
  },
);

export type DropdownMenuCheckboxItemProps = ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.CheckboxItem
>;

export const DropdownMenuCheckboxItem = forwardRef<HTMLDivElement, DropdownMenuCheckboxItemProps>(
  function DropdownMenuCheckboxItem({ className, children, ...rest }, ref) {
    return (
      <DropdownMenuPrimitive.CheckboxItem
        ref={ref}
        className={cn(ITEM_BASE, 'pr-8 text-text-primary', className)}
        {...rest}
      >
        {children}
        <DropdownMenuPrimitive.ItemIndicator className="absolute right-2 flex items-center">
          <Check className="text-primary" aria-hidden="true" />
        </DropdownMenuPrimitive.ItemIndicator>
      </DropdownMenuPrimitive.CheckboxItem>
    );
  },
);

export function DropdownMenuLabel({ className, ...rest }: ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      className={cn(
        'px-2 py-1.5 text-2xs font-semibold uppercase tracking-wide text-text-tertiary',
        className,
      )}
      {...rest}
    />
  );
}

export function DropdownMenuSeparator({ className }: { className?: string }) {
  return (
    <DropdownMenuPrimitive.Separator
      className={cn('-mx-1 my-1 h-px bg-border-subtle', className)}
    />
  );
}

export function DropdownMenuShortcut({ className, ...rest }: ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      className={cn('ml-auto text-2xs tracking-widest text-text-tertiary', className)}
      {...rest}
    />
  );
}
